import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { noop } from "lodash";

import { GraphMetricCard } from "@similarweb/pro-logic-components";
import type { AreaGraphProps } from "@similarweb/pro-logic-components/dist/graphs";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { percentageFilter } from "app/filters/ngFilters";
import { CardBenchmarkRow } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/CardBenchmarkRow";
import {
    getGraphDataFromArray,
    hasInfluenceCoreAverage,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/citations-overview/utils";
import { useCitationsOverview } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/hooks/useCitationsOverviewData";
import type { IInfluenceMetricCardProps } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/types";
import {
    getBaseWidgetRequestParams,
    getTopicsFilterParam,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

export const InfluenceMetricCard = ({
    campaign,
    campaignConfig,
    reportStatus,
    reportDuration,
    graphProps,
}: IInfluenceMetricCardProps) => {
    const {
        params: { topic: queryTopic },
    } = useCurrentStateAndParams();

    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;
    const topics = getTopicsFilterParam(queryTopic);

    const { data, isLoading, isError } = useCitationsOverview(
        {
            ...getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
            topics,
        },
        { enabled: canLoadData },
    );

    const hasData = !!data && !isError && reportStatus !== ReportStatus.Error;
    const shouldShowLoading = (isLoading || !campaign) && reportStatus !== ReportStatus.Error;

    const graphData = useMemo<AreaGraphProps<unknown>["data"] | null>(
        () =>
            hasData && data?.domainInfluence?.dailyData?.length
                ? getGraphDataFromArray(data.domainInfluence.dailyData, "domain-influence")
                : null,
        [data, hasData],
    );

    const influencePercent = (hasData ? percentageFilter()(data.domainInfluence?.value, 2) : "") as string;
    const textKey =
        campaign.brands.length > 1
            ? "llm.sources.overview.card.influence.text.multiple_brands"
            : "llm.sources.overview.card.influence.text";

    const showCoreAverage = hasInfluenceCoreAverage(data);

    const secondaryValue = hasData ? (
        <Stack gap="2px">
            <Text fontVariant="caption">
                {translate(textKey, {
                    brand: campaign.brands?.[0],
                    influence: influencePercent,
                })}
            </Text>
            {showCoreAverage ? (
                <CardBenchmarkRow
                    label={translate("llm.sources.overview.card.influence.core_average")}
                    formattedValue={(data.competitorBenchmark.influenceScoreAvg * 100).toFixed(2)}
                    dataAutomation="citations-influence-core-average"
                />
            ) : null}
        </Stack>
    ) : null;

    return (
        <GraphMetricCard
            title={translate("llm.sources.overview.card.influence.title")}
            tooltip={translate("llm.sources.overview.card.influence.tooltip")}
            value={influencePercent || null}
            secondaryValue={secondaryValue}
            graphProps={{
                ...graphProps,
                tooltipValueFormatter: (value: number) => percentageFilter()(value, 2),
                data: graphData,
            }}
            isLoading={shouldShowLoading}
            onCtaClick={noop}
        />
    );
};
